import React from 'react';
import { Collapse } from 'antd';
import {
  ClearedLink,
  ClearedExternalLink,
  CollapseIcon,
} from '../../common/controls/Controls';
import { useTranslation } from 'react-i18next';
import {
  toggleCMSNavigationGroup,
  toggleCMSSidebar,
} from '../../../redux/slices/localStorageSlice';
import { MOBILE_MAX_WIDTH } from '../../../utils/constants/common_constants.js';
import {
  ROLE_ING_PROJECTS_ADMIN,
  ROLE_ING_PROJECTS_GUARANTOR,
  ROLE_ING_PROJECT_STUDENT,
  ROLE_STUDY_PROGRAM_GUARANTEE,
  ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR,
} from '../../../utils/constants/roles.js';
import { useLocation } from 'react-router-dom';
import Tooltip from '../../common/Tooltip/Tooltip';

const isMobileWidth = () =>
  typeof window !== 'undefined' && window.innerWidth <= MOBILE_MAX_WIDTH;

const CollapsibleMenu = ({
  groupItem,
  index,
  isSidebarHidden,
  isActualOpened,
  dispatch,
  user,
  isAllowedToSeePages,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname || '';

  const hasRoleAccess = (roles) =>
    Array.isArray(roles) &&
    roles.length > 0 &&
    roles.some((role) =>
      (user?.roles || []).some((userRole) => userRole?.name === role),
    );

  const getSubItems = (items) => {
    return items
      .filter((subItem) => {
        if (
          subItem.permission &&
          (typeof subItem.permission === 'string'
            ? subItem.permission.includes('articles.index')
            : subItem.permission.some((permission) =>
                permission.includes('articles.index'),
              ))
        ) {
          return !isAllowedToSeePages;
        }

        if (!subItem.permission) {
          return !subItem.roles || hasRoleAccess(subItem.roles);
        }

        const permissions = Array.isArray(subItem.permission)
          ? subItem.permission
          : [subItem.permission];

        const getRoleForPermission = (permission) => {
          switch (permission) {
            case 'ing-projects-admin':
              return ROLE_ING_PROJECTS_ADMIN;
            case 'ing-projects-project-guarantee':
              return ROLE_ING_PROJECTS_GUARANTOR;
            case 'ing-projects-study-program-guarantee':
              return ROLE_STUDY_PROGRAM_GUARANTEE;
            case 'ing-projects-assignments':
              return ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR;
            case 'ing-projects-student':
              return ROLE_ING_PROJECT_STUDENT;
            default:
              return null;
          }
        };

        return permissions.some((permission) => {
          if (permission && user?.permissions?.[permission]) {
            return true;
          }

          const mappedRoleName = getRoleForPermission(permission);
          return mappedRoleName
            ? (user?.roles || []).some((role) => role?.name === mappedRoleName)
            : false;
        });
      })
      .map((subItem, index) => {
        const compositeKey = `${subItem.title}-${index}`;
        const isActive =
          typeof subItem.url === 'string' &&
          !subItem.url.includes('://') &&
          pathname.startsWith(subItem.url);
        return (
          <div
            key={compositeKey}
            className={`menu-sub-item ${isActive ? 'active-item' : ''}`}
          >
            {subItem.url.includes('.fri.uniza.sk') ? (
              <ClearedExternalLink
                href={subItem.url}
                onClick={(e) => {
                  // Close sidebar on mobile after navigation
                  e.stopPropagation();
                  if (isMobileWidth()) {
                    dispatch(toggleCMSSidebar(true));
                  }
                }}
              >
                {t(subItem.title)}
              </ClearedExternalLink>
            ) : (
              <ClearedLink
                to={subItem.url}
                onClick={(e) => {
                  // Close sidebar on mobile after navigation
                  e.stopPropagation();
                  if (isMobileWidth()) {
                    dispatch(toggleCMSSidebar(true));
                  }
                }}
              >
                {t(subItem.title)}
              </ClearedLink>
            )}
          </div>
        );
      });
  };

  const items = [
    {
      key: index,
      label: (
        <div
          onClick={() => {
            dispatch(
              toggleCMSNavigationGroup({
                index,
                // clicking the icon of a collapsed sidebar expands it and opens the group
                value: isSidebarHidden ? true : !isActualOpened,
                expandSidebar: true,
              }),
            );
          }}
          className="menu-group-container"
        >
          <Tooltip
            tooltip={isSidebarHidden ? t(groupItem.title) : undefined}
            right
          >
            <span style={{ marginRight: isSidebarHidden ? '0' : '10px' }}>
              {groupItem.icon}
            </span>
          </Tooltip>
          <div className="group-text">{t(groupItem.title)}</div>
          {!isSidebarHidden && <CollapseIcon open={isActualOpened} />}
        </div>
      ),
      children: getSubItems(groupItem.items),
      showArrow: false,
    },
  ];

  return (
    <Collapse
      items={items}
      bordered={false}
      activeKey={isActualOpened && !isSidebarHidden ? [index] : []}
    />
  );
};

export default CollapsibleMenu;
